import getEditor from 'ub/utils/editor-utils.js';
import calculateOffset from './calculate-offset';

const HORIZONTAL = 'horizontal';
const VERTICAL = 'vertical';

const refreshSelection = () => {
  const editor = getEditor();
  const activeElement = editor.activeElement;

  if (activeElement.type === 'lp-multi-select') {
    activeElement.fitToSelection();
    activeElement.updateOffsets();
  }

  editor.scrollToElement(activeElement);
};

const getContainerProperties = () => {
  const activeElement = getEditor().activeElement;
  const isMultiSelect = activeElement.type === 'lp-multi-select';

  const containerElm = isMultiSelect ? activeElement : activeElement.parentElement;

  const geometry = containerElm.model.safeGet('geometry');
  const size = containerElm.model.getSize();

  // Box is a special case where elements can actually be nested within.
  // Because of this we don't factor in their offset within the page section
  const offset = containerElm.type === 'lp-pom-box' ? { top: 0, left: 0 } : geometry.offset;

  const selectedElms = isMultiSelect ? activeElement.selection : [activeElement];

  return {
    selectedElms,
    containerGeometry: {
      offset,
      size
    }
  };
};

const setOffset = (elm, newOffset, originalOffset) => {
  const undoManager = getEditor().undoManager;

  undoManager.registerUndo({
    action: setOffset,
    receiver: { setOffset },
    params: [elm, originalOffset, newOffset, undoManager]
  });

  elm.model.setOffset(newOffset);

  refreshSelection();
};

const applyOffsets = (elementDataArr) => {
  const undoManager = getEditor().undoManager;

  undoManager.startGroup();

  elementDataArr.forEach(({elm, newOffset, originalOffset}) => {
    setOffset(elm, newOffset, originalOffset);
  });

  undoManager.endGroup();
};


const align = (alignDirection) => {
  const {selectedElms, containerGeometry} = getContainerProperties();

  const elementDataArr = getGeometryFromElms(selectedElms)
    .map(({elm, offset, size}) => {
      return {
        elm,
        originalOffset: offset,
        newOffset: calculateOffset[alignDirection](containerGeometry, {offset, size})
      };
    });

  applyOffsets(elementDataArr);
};

const getGeometryFromElms = (elms) => {
  return elms.map(elm => {
    const {offset, size, scale} = elm.model.safeGet('geometry');

    const scaleSize = {
      height: Math.round(size.height * scale),
      width: Math.round(size.width * scale)
    };

    return {
      offset,
      size: scaleSize,
      elm
    };
  });
};

const calculateTotalSpacing = (elmsGeom, direction) => {
  const { totalSpacing } = elmsGeom.reduce((prev, curr) => {
    const prevTotalSpacing = typeof prev.totalSpacing === 'undefined' ?
      0 : prev.totalSpacing;

    const elmSpacing = direction === HORIZONTAL ?
      curr.offset.left - (prev.offset.left + prev.size.width) :
      curr.offset.top - (prev.offset.top + prev.size.height);

    return {
      offset: curr.offset,
      size: curr.size,
      totalSpacing: prevTotalSpacing + elmSpacing,
    };
  });

  return totalSpacing;
};

const calculateElmGeomAndSpacing = (direction) => {
  const {selectedElms} = getContainerProperties();

  const elmsGeom = getGeometryFromElms(selectedElms);

  if (direction === HORIZONTAL) {
    elmsGeom.sort((curr, next) => curr.offset.left - next.offset.left);
  } else {
    elmsGeom.sort((curr, next) => curr.offset.top - next.offset.top);
  }

  const totalSpacing = calculateTotalSpacing(elmsGeom, direction);

  const offsetCount = elmsGeom.length - 1;

  const spacing = Math.ceil(totalSpacing / offsetCount);

  return {
    spacing,
    elmsGeom
  };
};

const calcOffsetWithSpacing = (prev, curr, spacing, direction) => {
  if (direction === HORIZONTAL) {
    return {
      left: prev.offset.left + prev.size.width + spacing,
      top: curr.offset.top
    };
  } else {
    return {
      left: curr.offset.left,
      top: prev.offset.top + prev.size.height + spacing
    };
  }
};

const distribute = (direction) => {
  const {spacing, elmsGeom} = calculateElmGeomAndSpacing(direction);

  const { elmDataArr } = elmsGeom.reduce((prev, curr) => {
    const prevElmDataArr = prev.elmDataArr || [];

    const newOffset = calcOffsetWithSpacing(prev, curr, spacing, direction);

    prevElmDataArr.push({
      elm: curr.elm,
      originalOffset: curr.offset,
      newOffset
    });

    return {
      elm: curr,
      offset: newOffset,
      size: curr.size,
      elmDataArr: prevElmDataArr
    };
  });

  applyOffsets(elmDataArr);
};

const distributeHorizontal = () => distribute(HORIZONTAL);

const distributeVertical = () => distribute(VERTICAL);

export default {
  align,
  distributeHorizontal,
  distributeVertical
};



// WEBPACK FOOTER //
// ./src/ui/canvas/element-alignment.js